import type { HeroId } from "@rivalwatch/shared";
import { CLIENT_HEROES } from "../../heroes/index.ts";
import type { Cue } from "../engine.ts";
import { baked } from "../bake.ts";
import { TRIM } from "../levels.ts";
import { bell, gainOut, click, clinks, cymbal, fm, later, noise, semis, thump, tone, trim, vary, whoosh } from "../synth.ts";
import { hz, playTheme, SCALE, STREAK_STEPS } from "../theory.ts";

// ------------------------------------------------------------------ you hit something

/** Body hit: a crisp tick. Chained hits nudge up by a quarter-tone, capped. */
/** Body hit, baked once; combos pitch it up a quarter-tone per chained hit. */
export const hitTick = (combo: number) => baked("hit", () => hitTickLive(0), { variants: 3, jitter: 0.004, rate: semis(Math.min(combo, 8) * 0.5) });

const hitTickLive =
  (combo: number): Cue =>
  trim(TRIM.feedback.hit, (o) => {
    const f = 2350 * semis(Math.min(combo, 8) * 0.5);
    tone(o, { f, gain: 0.15, d: 0.035 });
    tone(o, { f: f * 2.01, gain: 0.035, d: 0.02 });
    click(o, 0.18, 6000, 0.002);
    return 0.08;
  });

/** Critical: harder ink, a steel ring. It should feel better than a body hit, not just louder. */
const critTickLive: Cue = trim(TRIM.feedback.crit, (o) => {
  click(o, 0.42, 3000, 0.004);
  fm(o, { f: 3100, ratio: 1.5, index: 2, index1: 0, gain: 0.13, d: 0.15 });
  tone(o, { f: 1600, gain: 0.09, d: 0.05 });
  noise(o, { color: "white", type: "bandpass", f: 5200, q: 1, gain: 0.11, d: 0.05 });
  return 0.22;
});

/** KO: a body-blow thud, then a glass chime whose root climbs with the streak. */
export const ko =
  (streak: number): Cue =>
  trim(TRIM.feedback.ko, (o) => {
    const root = STREAK_STEPS[Math.min(Math.max(streak, 1), STREAK_STEPS.length) - 1]!;
    thump(o, 0.65, 95, 38, 0.17);
    click(o, 0.35, 2200, 0.012);
    noise(o, { color: "crackle", type: "bandpass", f: 3000, gain: 0.25, d: 0.08 });
    bell(later(o, 0.02), hz(24 + root), 0.3, 0.95);
    bell(later(o, 0.075), hz(31 + root), 0.35, 0.8);
    if (streak >= 3) bell(later(o, 0.13), hz(36 + root), 0.4, 0.7);
    if (streak >= 5) cymbal(later(o, 0.02), 0.35, 0.9);
    return 1.1;
  });

/** Assist: two bright notes meeting, like a handshake — you set it up, a teammate finished it. */
export const assist: Cue = trim(TRIM.feedback.assist, (o) => {
  bell(o, hz(19), 0.12, 0.6);
  bell(later(o, 0.05), hz(26), 0.14, 0.55);
  thump(o, 0.22, 170, 110, 0.05);
  click(o, 0.08, 3000, 0.003);
  return 0.7;
});

/** You healed an ally: a soft bell stepping through the heal pentatonic. */
export const healTick = (combo: number) => baked("heal", () => healTickLive(0), { variants: 2, jitter: 0, rate: semis(SCALE.heal[combo % SCALE.heal.length]!) });

const healTickLive =
  (combo: number): Cue =>
  trim(TRIM.feedback.heal, (o) => {
    const s = SCALE.heal[combo % SCALE.heal.length]! + 24;
    bell(o, hz(s), 0.08, 0.32);
    tone(o, { f: hz(s + 12), gain: 0.015, d: 0.1 });
    return 0.45;
  });

// ------------------------------------------------------------------ something hit you

export type Layer = "shield" | "armor" | "health";

/**
 * Damage taken, by the layer that absorbed it: shields crackle, armor clanks, health thuds.
 * `amount` 0..1 scales weight (a sniper round lands heavier than a pellet).
 */
export const hurt = (layer: Layer, amount: number) => {
  const heavy = amount > 0.6;
  return baked(`hurt:${layer}:${heavy ? 1 : 0}`, () => hurtLive(layer, heavy ? 0.9 : 0.35), { variants: 3, jitter: 0.03 });
};

const hurtLive =
  (layer: Layer, amount: number): Cue =>
  (outer) => {
    const k = 0.5 + 0.5 * Math.min(1, amount);
    const o = gainOut(outer, 10 ** (TRIM.hurt[layer] / 20));
    if (layer === "shield") {
      noise(o, { color: "white", type: "bandpass", f: vary(3600, 0.1), q: 1.4, gain: 0.22 * k, d: 0.07 });
      tone(o, { type: "square", f: 1250, f1: 780, glide: 0.05, gain: 0.03 * k, d: 0.06, lp: 3000 });
      noise(o, { color: "crackle", type: "highpass", f: 3000, gain: 0.35 * k, d: 0.1 });
    } else if (layer === "armor") {
      fm(o, { f: vary(520, 0.08), ratio: 1.63, index: 4, index1: 0, gain: 0.16 * k, d: 0.13 });
      click(o, 0.25 * k, 2500, 0.005);
      thump(o, 0.2 * k, 150, 80, 0.06);
    } else {
      thump(o, 0.45 * k, 120, 55, 0.1);
      noise(o, { color: "pink", type: "lowpass", f: 650, gain: 0.3 * k, d: 0.09 });
      noise(o, { color: "pink", type: "bandpass", f: 850, q: 1.3, gain: 0.14 * k, d: 0.05 });
    }
    if (amount > 0.6) thump(outer, 0.35, 70, 34, 0.22);
    return 0.35;
  };

/** An ally healed you. */
export const healed: Cue = trim(TRIM.feedback.healed, (o) => {
  tone(o, { f: 1170, f1: 1760, glide: 0.25, gain: 0.03, a: 0.04, d: 0.25 });
  bell(later(o, 0.05), hz(28), 0.2, 0.25);
  return 0.6;
});

/** Your ultimate is charged: a gold Lydian run, then the first notes of your theme. */
export const ultReady =
  (hero: HeroId): Cue =>
  trim(TRIM.feedback.ultReady, (o) => {
    SCALE.ult.forEach((s, i) => bell(later(o, i * 0.04), hz(s + 12), 0.2, 0.5));
    cymbal(o, 0.3, 0.6, true);
    whoosh(o, 0.12, 600, 3500, 0.5);
    return 0.35 + playTheme(later(o, 0.36), hero, "tease");
  });

export const death: Cue = trim(TRIM.feedback.death, (o) => {
  thump(o, 0.8, 70, 26, 0.55);
  noise(o, { color: "pink", type: "lowpass", f: 900, f1: 120, gain: 0.45, d: 0.6 });
  [7, 3, 0].forEach((s, i) => tone(later(o, 0.12 + i * 0.16), { type: "triangle", f: hz(s - 12), gain: 0.07, a: 0.02, d: 0.5 }));
  return 1.4;
});

export const respawn: Cue = trim(TRIM.feedback.respawn, (o) => {
  whoosh(o, 0.3, 300, 3200, 0.55);
  bell(later(o, 0.4), hz(12), 0.3, 0.5);
  bell(later(o, 0.44), hz(19), 0.3, 0.45);
  thump(later(o, 0.4), 0.3, 100, 50, 0.2);
  return 1.2;
});

export type StatusCue = "stun" | "sleep" | "antiHeal" | "boost" | "linked";

export const status = (kind: StatusCue): Cue => trim(TRIM.status[kind], statusRaw(kind));

const statusRaw = (kind: StatusCue): Cue => {
  switch (kind) {
    case "stun":
      // A comic "bonk", and the ring in your ears after it.
      return (o) => {
        thump(o, 0.5, 320, 140, 0.1);
        click(o, 0.3, 2500, 0.006);
        tone(o, { f: 3300, gain: 0.02, a: 0.05, d: 0.9 });
        tone(o, { f: 3360, gain: 0.015, a: 0.05, d: 0.9 });
        return 1.1;
      };
    case "sleep":
      return (o) => {
        [19, 16, 12].forEach((s, i) => bell(later(o, i * 0.18), hz(s), 0.35, 0.5));
        noise(o, { color: "pink", type: "bandpass", f: 900, f1: 350, q: 1.5, gain: 0.08, a: 0.2, d: 0.8, delay: 0.3 });
        return 1.4;
      };
    case "antiHeal":
      // Healing blocked: glass cracking, a sour semitone.
      return (o) => {
        clinks(o, 7, 0.05, 0.15, 3500, 7000);
        tone(o, { type: "triangle", f: 1100, gain: 0.05, d: 0.35 });
        tone(o, { type: "triangle", f: 1165, gain: 0.05, d: 0.35 });
        return 0.5;
      };
    case "boost":
      return (o) => {
        tone(o, { type: "sawtooth", f: 220, f1: 880, glide: 0.25, gain: 0.05, d: 0.3, lp: 2400 });
        bell(later(o, 0.22), hz(18), 0.3, 0.45);
        return 0.8;
      };
    case "linked":
      // Soul-linked: chains and a low growl.
      return (o) => {
        clinks(o, 8, 0.06, 0.3, 1200, 3000);
        fm(o, { f: 55, ratio: 1.5, index: 4, index1: 0.5, gain: 0.12, d: 0.6 });
        return 0.8;
      };
  }
};

// ------------------------------------------------------------------ bodies moving

export type Weight = "light" | "mid" | "heavy";
export const weightOf = (hero: HeroId): Weight => CLIENT_HEROES[hero].audio.weight;

/**
 * One footstep on Foundry concrete. `bright` lifts the scuff for enemies so they cut through
 * the mix: footsteps are information.
 */
export const footstep = (w: Weight, bright: boolean, side: number) => baked(`step:${w}:${bright ? 1 : 0}`, () => footstepLive(w, bright, 0), { variants: 6, jitter: 0.05, rate: side ? 1.03 : 0.97 });

const footstepLive =
  (w: Weight, bright: boolean, side: number): Cue =>
  trim(TRIM.step[w], (o) => {
    const k = w === "heavy" ? 1.35 : w === "light" ? 0.7 : 1;
    const p = vary(1, 0.08) * (side ? 1.03 : 0.97);
    thump(o, 0.3 * k, (w === "heavy" ? 72 : 92) * p, 44, 0.05 * k + 0.02);
    noise(o, { color: "pink", type: "lowpass", f: 520 * p, gain: 0.24 * k, d: 0.045 });
    noise(o, { color: "white", type: "bandpass", f: (bright ? 3100 : 2100) * p, q: 0.9, gain: (bright ? 0.18 : 0.1) * k, d: 0.03, delay: 0.012 });
    noise(o, { color: "crackle", type: "highpass", f: 2500, gain: 0.12, d: 0.03, delay: 0.004 });
    if (w === "heavy") fm(o, { f: 190 * p, ratio: 1.5, index: 1.5, index1: 0, gain: 0.05, d: 0.08, delay: 0.01 });
    if (w === "light") click(o, 0.06, 5200, 0.002, 0.02);
    return 0.2;
  });

export const jump = (w: Weight) => baked(`jump:${w}`, () => jumpLive(w), { variants: 3, jitter: 0.05 });

const jumpLive =
  (w: Weight): Cue =>
  trim(TRIM.jump[w], (o) => {
    whoosh(o, w === "heavy" ? 0.2 : 0.14, 700, 1700, 0.12);
    noise(o, { color: "pink", type: "lowpass", f: 400, gain: 0.1, d: 0.04 });
    return 0.2;
  });

/** Landing, scaled by how hard you came down (m/s). */
/** Landings in three strengths per weight class. */
export const land = (w: Weight, speed: number) => {
  const cls = speed < 7 ? 5 : speed < 12 ? 9 : 15;
  return baked(`land:${w}:${cls}`, () => landLive(w, cls), { variants: 3, jitter: 0.04 });
};

const landLive =
  (w: Weight, speed: number): Cue =>
  trim(TRIM.land[w], (o) => {
    const k = Math.min(1.4, 0.35 + speed / 14) * (w === "heavy" ? 1.3 : w === "light" ? 0.75 : 1);
    thump(o, 0.45 * k, 90, 36, 0.1 + 0.06 * k);
    noise(o, { color: "pink", type: "lowpass", f: 700, gain: 0.3 * k, d: 0.08 });
    noise(o, { color: "crackle", type: "bandpass", f: 2200, q: 0.7, gain: 0.25 * k, d: 0.1 });
    if (w === "heavy") fm(o, { f: 160, ratio: 1.5, index: 2, index1: 0, gain: 0.08 * k, d: 0.12 });
    return 0.35;
  });

export const critTick = baked("crit", () => critTickLive, { variants: 3, jitter: 0.01 });
